import React, { useMemo, useState } from 'react';

// Reusable grid picker shown under a field.
// rows: [{ value, ...fields }]   columns: [{ key, label, width?, bold?, sub? }]  (sub = key of a smaller second line)
export default function GridPicker({ rows, columns, onSelect, onClose, selectedValue, placeholder, emptyText }) {
    const [q, setQ] = useState('');
    const [sort, setSort] = useState({ key: columns[0].key, dir: 1 });
    const shown = useMemo(() => {
        const s = q.toLowerCase().trim();
        const keys = columns.flatMap((c) => [c.key, c.sub]).filter(Boolean);
        return (rows || [])
            .filter((r) => !s || keys.some((k) => String(r[k] == null ? '' : r[k]).toLowerCase().includes(s)))
            .slice()
            .sort((a, b) => sort.dir * String(a[sort.key] == null ? '' : a[sort.key]).localeCompare(String(b[sort.key] == null ? '' : b[sort.key]), undefined, { numeric: true }));
    }, [rows, columns, q, sort]);
    const th = { position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1, cursor: 'pointer', whiteSpace: 'nowrap' };
    return (
        <div style={{ border: '1px solid #ced4da', borderRadius: 6, marginTop: 4, background: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 8, borderBottom: '1px solid #e9ecef' }}>
                <i className="fas fa-search" style={{ color: '#6c757d' }}></i>
                <input
                    autoFocus
                    className="form-control form-control-sm"
                    placeholder={placeholder || 'Search…'}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') { e.preventDefault(); onClose(); }
                        if (e.key === 'Enter') { e.preventDefault(); if (shown[0]) onSelect(shown[0]); }
                    }}
                />
                <span style={{ fontSize: 12, color: '#6c757d', whiteSpace: 'nowrap' }}>{shown.length} found</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} style={{ ...th, width: c.width }} onClick={() => setSort({ key: c.key, dir: sort.key === c.key ? -sort.dir : 1 })}>
                                    {c.label} {sort.key === c.key ? (sort.dir > 0 ? '▲' : '▼') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!shown.length && (
                            <tr><td colSpan={columns.length} className="text-center text-muted p-3">{(rows || []).length ? 'No match.' : (emptyText || 'Nothing to choose from.')}</td></tr>
                        )}
                        {shown.length > 200 && (
                            <tr><td colSpan={columns.length} className="text-center text-muted p-2" style={{ fontSize: 12 }}>Showing first 200 of {shown.length}: refine your search</td></tr>
                        )}
                        {shown.slice(0, 200).map((r) => (
                            <tr key={r.value} onClick={() => onSelect(r)} style={{ cursor: 'pointer', background: r.value === selectedValue ? '#e7f1ff' : undefined }}>
                                {columns.map((c) => (
                                    <td key={c.key} style={{ color: c.bold ? undefined : '#495057' }}>
                                        <div style={{ fontWeight: c.bold ? 600 : 400 }}>{r[c.key] === '' || r[c.key] == null ? '—' : r[c.key]}</div>
                                        {c.sub && r[c.sub] ? <small className="text-muted" title={r[c.sub]} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r[c.sub]}</small> : null}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
